import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { Spin, message, Divider } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import HeadLogo from "../Assets/Group 619.svg";
import { loginApi } from "../services/auth";
import "react-toastify/dist/ReactToastify.css"; 
import { GoogleLogin } from '@react-oauth/google';


export default function Login() {
const [details, setDetails] = useState({ email_id: "", password: "" });
const [isLoader, setIsLoader] = useState(false);
const [showPassword, setShowPassword] = useState(false);
const navigate = useNavigate();
const location = useLocation();
const antIcon = <LoadingOutlined style={{ fontSize: 24, color: "#fff" }} spin />;

const POST_AUTH_REDIRECT_KEY = "post_auth_redirect";
const AUTH_SOURCE_KEY = "auth_source";
const PENDING_GOOGLE_SETUP_KEY = "pending_google_business_setup";

const isSafeInternalRedirect = (value) => {
if (!value) return false;
return value.startsWith("/") && !value.startsWith("//");
};

const saveMiniToolRedirect = (search) => {
const params = new URLSearchParams(search);
const source = params.get("source");
const redirect = params.get("redirect");

if (source === "mini-tools" && isSafeInternalRedirect(redirect)) {
sessionStorage.setItem(AUTH_SOURCE_KEY, source);
sessionStorage.setItem(POST_AUTH_REDIRECT_KEY, redirect);
}
};

const getPostAuthRedirect = (fallback = "/") => {
const redirect = sessionStorage.getItem(POST_AUTH_REDIRECT_KEY);
return isSafeInternalRedirect(redirect) ? redirect : fallback;
};

const preserveMiniToolRedirectAndClearStorage = () => {
const savedSource = sessionStorage.getItem(AUTH_SOURCE_KEY);
const savedRedirect = sessionStorage.getItem(POST_AUTH_REDIRECT_KEY);

localStorage.clear();
sessionStorage.clear();

if (savedSource) {
sessionStorage.setItem(AUTH_SOURCE_KEY, savedSource);
}

if (savedRedirect) {
sessionStorage.setItem(POST_AUTH_REDIRECT_KEY, savedRedirect);
}
};

const handleGoogleSuccess = async (credentialResponse) => {
    setIsLoader(true);

    try {
        const response = await axios.post(
            `${process.env.REACT_APP_BACKEND_API}/user/auth/google`,
            {
                token: credentialResponse.credential,
            }
        );

        const {
            requiresBusinessSetup,
            googleSetupToken,
            googleUser,
            token,
            user,
        } = response.data.data;

        if (requiresBusinessSetup) {
            const pendingSetup = {
                googleSetupToken,
                user: googleUser,
            };

            sessionStorage.setItem(
                PENDING_GOOGLE_SETUP_KEY,
                JSON.stringify(pendingSetup)
            );

            localStorage.removeItem("user_Token");
            sessionStorage.removeItem("user_Token");
            localStorage.removeItem("user_id");

            message.success(
                "Google authentication successful. Complete your company details."
            );

            navigate("/Signup", {
                replace: true,
                state: {
                    googleSetupPending: true,
                },
            });

            return;
        }

        if (!token || !user) {
            throw new Error(
                "Google authentication returned an invalid response."
            );
        }

        localStorage.setItem("user_Token", token);
        localStorage.setItem("Email", user.email_id);
        localStorage.setItem("user_id", user._id);

        sessionStorage.removeItem(
            PENDING_GOOGLE_SETUP_KEY
        );

        message.success("Authentication successful!");
        window.location.href = getPostAuthRedirect("/");
    } catch (error) {
        toast.error(
            error.response?.data?.message ||
            error.message ||
            "Google Sign-In failed."
        );
    } finally {
        setIsLoader(false);
    }
};

const handleGoogleError = () => {
    toast.error("Google authentication failed. Please try again.");
};

const handleInputChange = (e) => {
const { id, value } = e.target;
setDetails((prev) => ({ ...prev, [id]: value }));
};

const validateInputs = () => {
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
if (!details.email_id) {
toast.error("Please enter your email address.");
return false;
}
if (!emailRegex.test(details.email_id)) {
toast.error("Please enter a valid email address.");
return false;
}
if (!details.password) {
toast.error("Please enter your password.");
return false;
}
return true;
};

const handleLogin = async (e) => {
e.preventDefault();
if (!validateInputs()) return;

setIsLoader(true);
try {
const response = await loginApi(details);

if (response.error) {
if (response.data.errorType === "email") {
toast.error("Please verify your email first.");
localStorage.setItem("Email", details.email_id);
navigate("/verify-otp");
} else {
message.error(response.data?.message || "Login failed. Please check your credentials.");
}
return;
}

const { token, user } = response.data;
localStorage.setItem("user_Token", token);
localStorage.setItem("Email", user.email_id);
localStorage.setItem("user_id", user._id);

window.location.href = getPostAuthRedirect("/");

} catch (error) {
message.error("An unexpected error occurred. Please try again.");
console.error(error);
} finally {
setIsLoader(false);
}
};

useEffect(() => {
preserveMiniToolRedirectAndClearStorage();
}, []);

useEffect(() => {
saveMiniToolRedirect(location.search);
}, [location.search]);

return (
<div className="min-h-screen flex flex-col items-center justify-center p-4 bg-neutral-light">
<ToastContainer position="top-right" autoClose={5000} />
<div className="auth-card">
<img src={HeadLogo} alt="Oolook Logo" className="mx-auto h-12 w-auto mb-6" />
<h2 className="text-center text-2xl font-bold text-neutral-dark mb-2">Welcome Back!</h2>
<p className="text-center text-sm text-neutral-muted mb-8">
Don't have an account? <Link to="/signup" className="link">Sign up now</Link>
</p>

<div className="flex justify-center">
    <GoogleLogin
        onSuccess={handleGoogleSuccess}
        onError={handleGoogleError}
        text="signin_with"
    />
</div>

<Divider className="my-6">OR</Divider>

<form onSubmit={handleLogin} className="space-y-4">
<div>
<label className="block text-sm font-medium text-neutral-muted mb-1">Email Address</label>
<input type="email" id="email_id" className="form-input" placeholder="you@example.com" onChange={handleInputChange} />
</div>

<div>
<div className="flex justify-between items-center mb-1">
<label className="block text-sm font-medium text-neutral-muted">Password</label>
<Link to="/forgot-password" className="link text-sm">Forgot Password?</Link>
</div>
<div className="relative">
<input
type={showPassword ? "text" : "password"}
id="password"
className="form-input pr-10"
placeholder="Enter your password"
onChange={handleInputChange}
/>
<button
type="button"
onClick={() => setShowPassword(!showPassword)}
className="absolute inset-y-0 right-0 px-3 flex items-center text-neutral-muted"
aria-label={showPassword ? "Hide password" : "Show password"}
>
<i className={`fa-regular ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
</button>
</div>
</div>

<div className="pt-4">
<button type="submit" className="btn-primary" disabled={isLoader}>
{isLoader ? <Spin indicator={antIcon} /> : "Log In"}
</button>
</div>
<p className="text-xs text-center text-neutral-muted px-4">
    By signing in, you agree to the{" "}
    <a href="https://oolook.in/terms" target="_blank" rel="noopener noreferrer" className="link">
        Terms of Service
    </a> and {" "}
    <a href="https://oolook.in/privacy" target="_blank" rel="noopener noreferrer" className="link">
        Privacy Policy
    </a>.
</p>
</form>
</div>
</div>
);
}